/**
 * Notifications: the bell's history and its unread count.
 */
import { get, post } from '@/api/client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * The caller's own notifications in the active company, the history behind the bell (plan
 * notifications 4.5). `web_url` is a web route or null; `url` is the phone's link.
 */
export interface ApiNotification {
  id: number;
  kind: string | null;
  title: string;
  message: string;
  url: string | null;
  web_url: string | null;
  is_read: boolean;
  created_at: string;
}
const NOTIFICATION_PAGE = 30;
/** Newest first, thirty at a time; the next page is the rows before the oldest one shown. */
export function useNotifications(enabled = true) {
  return useInfiniteQuery({
    queryKey: ['notifications', 'list'],
    enabled,
    initialPageParam: null as number | null,
    queryFn: async ({ pageParam }) => {
      const r = await get<ApiNotification[]>(
        `/notifications?${pageParam ? `before=${pageParam}&` : ''}limit=${NOTIFICATION_PAGE}`,
      );
      return Array.isArray(r) ? r : [];
    },
    getNextPageParam: (last) => (last.length < NOTIFICATION_PAGE ? null : (last.at(-1)?.id ?? null)),
    staleTime: 30_000,
  });
}
/** The bell's number: asked every minute and when the window comes back into focus. */
export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: async () => (await get<{ count?: number }>('/notifications/unread-count')).count ?? 0,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}
export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { ids: number[] } | { all: true }) => post<{ updated: number }>('/notifications/read', b),
    onSettled: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}
